import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useUpdateTenant, useUploadTenantSidebarImage } from "../../lib/api";
import { FieldLabel, GhostButton, SecondaryButton } from "./primitives";

// The picture at the foot of the dashboard sidebar, in both the collapsed rail
// and the expanded drawer. A brand with none keeps the plain sidebar.
export default function BrandSidebarImageEditor({ tenant }) {
  const upload = useUploadTenantSidebarImage(tenant.id);
  const update = useUpdateTenant();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const image = preview || tenant.sidebar_image_url || "";

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    try {
      await upload.mutateAsync(file);
      toast.success("Sidebar image updated.");
    } catch (err) {
      setPreview(null);
      toast.error(err?.response?.data?.detail || "Upload failed.");
    }
  };

  const onRemove = async () => {
    try {
      await update.mutateAsync({ id: tenant.id, sidebar_image_url: "" });
      setPreview(null);
      toast.success("Sidebar image removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove the sidebar image.");
    }
  };

  const busy = upload.isPending || update.isPending;

  return (
    <div className="mt-7 max-w-[560px]">
      <FieldLabel>Sidebar image</FieldLabel>
      <div className="flex items-center gap-4">
        <span className="flex h-24 w-16 flex-none items-center justify-center overflow-hidden rounded-2xl bg-[#1C467A] shadow-[0_0_0_1px_#E5E7EB]">
          {image ? (
            <img src={image} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="px-1 text-center text-[10px] font-medium text-white/60">No image</span>
          )}
        </span>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={onPick} className="hidden" data-testid="brand-sidebar-image-input" />
          <SecondaryButton onClick={() => fileRef.current?.click()} disabled={busy}>
            {busy && <Loader2 className="animate-spin" size={14} />}
            {image ? "Replace" : "Upload image"}
          </SecondaryButton>
          {image && <GhostButton onClick={onRemove} disabled={busy}>Remove</GhostButton>}
        </div>
      </div>
      <p className="mt-2 text-[12px] text-[#6B7280]">PNG, JPG or WEBP · up to 5 MB · shown at the bottom of the sidebar, cropped to fit</p>
    </div>
  );
}
